import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import AuthoredSectionBlock from './AuthoredSectionBlock';
import CoverDetailsBlock from './CoverDetailsBlock';
import {Note, Sections} from './common';
import type {PanelProps} from './types';

const SubPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const SubPanelTitle = styled.div`
	${({theme}) => theme.sys.font.subheading[2]}
	color: ${({theme}) => theme.sys.color.text.content.main};
`;

/**
 * The six document sub-blocks, which the POC surfaced as six separate top-level panels. Cover,
 * Estimate and Agreement are locked on and have nothing to configure here.
 */
const DocumentSectionsPanel: React.FC<PanelProps> = ({
	template,
	onChange,
	mergeFields,
	data,
	expiryDate,
	minExpiryDate,
	onExpiryChange,
	expiryError,
	contentBlocks,
	onSaveBlock,
	onSaveBlockAs,
	onSetBlockDefault,
}) => {
	const intl = useIntl();

	return (
		<Sections>
			<SubPanel>
				<SubPanelTitle>{intl.formatMessage({id: 'quote_designer.section.cover'})}</SubPanelTitle>
				<CoverDetailsBlock
					data={data}
					expiryDate={expiryDate}
					minExpiryDate={minExpiryDate}
					onExpiryChange={onExpiryChange}
					expiryError={expiryError}
				/>
			</SubPanel>

			<SubPanel>
				<AuthoredSectionBlock
					type="INTRODUCTION"
					template={template}
					onChange={onChange}
					mergeFields={mergeFields}
					blocks={contentBlocks}
					placeholderId="quote_designer.intro.placeholder"
					onSaveBlock={onSaveBlock}
					onSaveBlockAs={onSaveBlockAs}
					onSetBlockDefault={onSetBlockDefault}
				/>
			</SubPanel>

			<SubPanel>
				<AuthoredSectionBlock
					type="SCOPE"
					template={template}
					onChange={onChange}
					mergeFields={mergeFields}
					blocks={contentBlocks}
					placeholderId="quote_designer.scope.placeholder"
					onSaveBlock={onSaveBlock}
					onSaveBlockAs={onSaveBlockAs}
					onSetBlockDefault={onSetBlockDefault}
				/>
			</SubPanel>

			<SubPanel>
				<SubPanelTitle>{intl.formatMessage({id: 'quote_designer.section.estimate'})}</SubPanelTitle>
				<Note>{intl.formatMessage({id: 'quote_designer.sections.estimate_locked'})}</Note>
			</SubPanel>

			<SubPanel>
				<AuthoredSectionBlock
					type="TERMS"
					template={template}
					onChange={onChange}
					mergeFields={mergeFields}
					blocks={contentBlocks}
					placeholderId="quote_designer.terms.placeholder"
					onSaveBlock={onSaveBlock}
					onSaveBlockAs={onSaveBlockAs}
					onSetBlockDefault={onSetBlockDefault}
				/>
			</SubPanel>

			<SubPanel>
				<SubPanelTitle>{intl.formatMessage({id: 'quote_designer.section.agreement'})}</SubPanelTitle>
				<Note>{intl.formatMessage({id: 'quote_designer.sections.agreement_locked'})}</Note>
			</SubPanel>
		</Sections>
	);
};

export default DocumentSectionsPanel;
